import { Box, useTheme } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import MenuItemWrapper from '../../../../components/Dropdown/DropdownStyle';
import { VerticalDotsOutlineIcon } from '../../../../components/Icons/VerticalDotsIcon';
import MenuWrapper from '../../../../components/Menu/MenuStyle';
import UserDetailsContext from '../../../../contexts/UserDetailsContext/UserDetailsContext';
import usePortalConfig from '../../../../hooks/usePortalConfig/usePortalConfig';
import useRolePermissions from '../../../../hooks/useRolePermissions/useRolePermissions';
import {
  CancelScheduledOperationRequest,
  Device,
  GetDeviceByIdFn,
  GetDeviceFn,
  Sim,
  SimOperations,
} from '../../../../models/UserDevicesModels';
import {
  setDevice,
  updateRequest,
  updateSelectedDropdownUser,
  updateTypeOfConnection,
} from '../../../../stores/NewConnectionStore';
import { setDeviceToUpgrade } from '../../../../stores/UpgradeDeviceStore';
import NewConnectionDrawer from '../../components/NewConnectionDrawer/NewConnectionDrawer';
import RestoreConnectionDrawer from './RestoreConnectionDrawer/RestoreConnectionDrawer';
import SuspendSimDrawer from './SuspendSimDrawer/SuspendSimDrawer';
import TerminateConnectionDrawer from './TerminateConnectionDrawer/TerminateConnectionDrawer';
import UpgradeDeviceDrawer from './UpgradeDeviceDrawer/UpgradeDeviceDrawer';
import useModal from '../../../../hooks/useModal/useModal';

interface UserDetailsSimRowOptionsProps {
  sim: Sim;
  resendNotification: (deviceId: string) => void;
  isPerformingAction: boolean;
  getDeviceById: GetDeviceFn | GetDeviceByIdFn;
  onNewConnectionSuccess: () => void;
  onUpgradeSuccess: () => void;
  onSuspendSimSuccess: () => void;
  onRestoreConnectionSuccess: () => void;
  onTerminateConnectionSuccess: () => void;
  cancelScheduledOperation: (req: CancelScheduledOperationRequest) => void;
  onToggleDrawer: (isDrawerOpen: boolean) => void;
}
function UserDetailsSimRowOptions({
  sim,
  resendNotification,
  isPerformingAction,
  getDeviceById,
  onNewConnectionSuccess,
  onUpgradeSuccess,
  onSuspendSimSuccess,
  onRestoreConnectionSuccess,
  onTerminateConnectionSuccess,
  cancelScheduledOperation,
  onToggleDrawer,
}: UserDetailsSimRowOptionsProps) {
  const [t] = useTranslation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  // TODO: refactor this wall of state :-D
  const [showUpgradeDevice, setShowUpgradeDevice] = useState(false);
  const [showNewConnection, setShowNewConnection] = useState(false);
  const [showSuspendSim, setShowSuspendSim] = useState(false);
  const [showRestoreConnection, setShowRestoreConnection] = useState(false);
  const [showTerminateConnection, setShowTerminateConnection] = useState(false);
  const dispatch = useDispatch();
  const modal = useModal();
  const { isUserAllowed } = useRolePermissions();
  const user = useContext(UserDetailsContext);
  const { featurePermissions } = usePortalConfig();

  const isDrawerOpen =
    showUpgradeDevice || showNewConnection || showSuspendSim || showRestoreConnection || showTerminateConnection;

  /**
   * Watch drawer trigger variables to toggle z-index of the cell
   */
  useEffect(() => {
    onToggleDrawer(isDrawerOpen);
  }, [isDrawerOpen, onToggleDrawer]);

  /**
   * Handle button click
   * @param event
   */
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    dispatch(setDeviceToUpgrade({ device: getDeviceById(sim.deviceId) as Device, sim }));
    setAnchorEl(event.currentTarget);
  };

  /**
   * Handle menu close
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Initiates the new connection journey, beginning on the notifications step
   */
  const initNewConnectionJourney = () => {
    if (user) {
      handleClose();
      dispatch(
        updateSelectedDropdownUser({
          value: user.id,
          label: `${user.firstName} ${user.lastName}`,
          key: user.id,
          secondaryLabel: user.email,
          data: user,
        }),
      );
      dispatch(updateTypeOfConnection('ESIM'));
      dispatch(updateRequest({ userId: user?.id as string, eid: sim.simId }));
      dispatch(setDevice(getDeviceById(sim.deviceId) as Device));
      setShowNewConnection(true);
    }
  };

  /**
   * Inits the suspend sim journey
   */
  const initSuspendSimJourney = () => {
    handleClose();
    setShowSuspendSim(true);
  };

  /**
   * Inits the terminate connection journey
   */
  const initTerminateConnectionJourney = () => {
    handleClose();
    setShowTerminateConnection(true);
  };

  /**
   * Inits the restore connection journey
   */
  const initRestoreJourney = () => {
    handleClose();
    setShowRestoreConnection(true);
  };

  /**
   * Inits the cancel scheduled termination journey
   */
  const initCancelTerminationJourney = () => {
    handleClose();
    if (sim.status === 'SUSPENDED') {
      modal.showModal({
        modalTitle: t('pages.userDetails.tabs.simsTable.cancelTerminationModal.title'),
        modalDescription: t('pages.userDetails.tabs.simsTable.cancelTerminationModal.description'),
        confirmationModal: true,
        confirmationButtonLabel: t('common.confirm'),
        cancelButtonLabel: t('common.back'),
        onAccept: (setOpen) => {
          setOpen(false);
          cancelScheduledOperation({
            simId: sim.id,
            journeyCancellationType: 'CANCEL_TERMINATION',
          });
        },
      });
    } else {
      cancelScheduledOperation({
        simId: sim.id,
        journeyCancellationType: 'CANCEL_TERMINATION',
      });
    }
  };

  /**
   * Render upgrade option for current device - SIM swap
   */
  const renderUpgradeOption = () => {
    if (
      (isUserAllowed(featurePermissions.userDevicesActions) && sim.status === 'CONNECTED') ||
      (sim.status === 'SUSPENDED' && sim.journey !== 'TERMINATION')
    ) {
      return (
        <MenuItemWrapper
          onClick={() => {
            handleClose();
            setShowUpgradeDevice(true);
          }}
        >
          {t('pages.userDetails.tabs.devicesTable.options.upgradeDevice')}
        </MenuItemWrapper>
      );
    }
    return null;
  };

  /**
   * Render new connection option
   */
  const renderNewConnectionOption = () => {
    if (
      user &&
      isUserAllowed(featurePermissions.userDevicesActions) &&
      sim.operations &&
      sim.operations.includes(SimOperations.NEW_CONNECTION)
    ) {
      return (
        <MenuItemWrapper onClick={initNewConnectionJourney}>
          {t('pages.userDetails.tabs.devicesTable.options.newConnection')}
        </MenuItemWrapper>
      );
    }
    return null;
  };

  /**
   * Render resend option for current device
   */
  const renderResendOption = () => {
    if (isUserAllowed(featurePermissions.userDevicesActions) && sim.status === 'ERROR') {
      return (
        <MenuItemWrapper
          disabled={isPerformingAction || sessionStorage.getItem('impersonateId') !== null}
          onClick={() => {
            handleClose();
            resendNotification(sim.id);
          }}
        >
          {t('pages.userDetails.tabs.devicesTable.options.resend')}
        </MenuItemWrapper>
      );
    }
    return null;
  };

  /**
   * Render suspend SIM option
   */
  const renderSuspendSimOption = () => {
    if (isUserAllowed(featurePermissions.userDevicesActions) && sim.status === 'CONNECTED' && sim.type !== 'PSIM') {
      return (
        <MenuItemWrapper onClick={initSuspendSimJourney} sx={{ color: theme.palette.primary.main }}>
          {t('pages.userDetails.tabs.devicesTable.options.suspendSim')}
        </MenuItemWrapper>
      );
    }
    return null;
  };

  /**
   * Render terminate connection option
   */
  const renderTerminateConnection = () => {
    if (
      isUserAllowed(featurePermissions.userDevicesActions) &&
      sim.operations &&
      sim.operations.includes(SimOperations.TERMINATION)
    ) {
      return (
        <MenuItemWrapper onClick={initTerminateConnectionJourney} sx={{ color: theme.palette.primary.main }}>
          {t('pages.userDetails.tabs.devicesTable.options.terminateConnection')}
        </MenuItemWrapper>
      );
    }
    return null;
  };

  /**
   * Render restore option
   */
  const renderRestoreOption = () => {
    if (
      isUserAllowed(featurePermissions.userDevicesActions) &&
      sim.status === 'SUSPENDED' &&
      sim.journey !== 'TERMINATION'
    ) {
      return (
        <MenuItemWrapper onClick={initRestoreJourney}>
          {t('pages.userDetails.tabs.devicesTable.options.restore')}
        </MenuItemWrapper>
      );
    }
    return null;
  };

  /**
   * Render cancel scheduled provisioning option
   */
  const renderCancelProvisionOption = () => {
    if (
      isUserAllowed(featurePermissions.userDevicesActions) &&
      sim.operations &&
      sim.operations.includes(SimOperations.CANCEL_SCHEDULED_PROVISION)
    ) {
      return (
        <MenuItemWrapper
          onClick={() => {
            handleClose();
            cancelScheduledOperation({
              simId: sim.id,
              journeyCancellationType: 'CANCEL_PROVISIONING',
            });
          }}
          sx={{ color: theme.palette.primary.main }}
          disabled={isPerformingAction || sessionStorage.getItem('impersonateId') !== null}
        >
          {t('pages.userDetails.tabs.devicesTable.options.cancelProvision')}
        </MenuItemWrapper>
      );
    }
    return null;
  };

  /**
   * Render cancel scheduled termination option
   */
  const renderCancelTerminationOption = () => {
    if (
      isUserAllowed(featurePermissions.userDevicesActions) &&
      sim.operations &&
      sim.operations.includes(SimOperations.CANCEL_SCHEDULED_TERMINATION)
    ) {
      return (
        <MenuItemWrapper
          onClick={initCancelTerminationJourney}
          sx={{ color: theme.palette.primary.main }}
          disabled={isPerformingAction || sessionStorage.getItem('impersonateId') !== null}
        >
          {t('pages.userDetails.tabs.devicesTable.options.cancelTermination')}
        </MenuItemWrapper>
      );
    }
    return null;
  };

  // TODO - when BE is returning all sim operations, only show menu items when array is not empty
  /**
   * Validation to know if we can render the options menu
   */
  const canRenderOptions = () =>
    isUserAllowed(featurePermissions.userDevicesActions) &&
    (sim.operations.length > 0 ||
      sim.status === 'CONNECTED' ||
      sim.status === 'ERROR' ||
      (sim.status === 'SUSPENDED' && sim.journey !== 'TERMINATION'));

  return (
    <>
      {canRenderOptions() ? (
        <Box
          id={`box-${sim.id}`}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '0.375rem',
            width: '100%',
            height: '2.25rem',
            flexShrink: 0,
            ':hover': { backgroundColor: theme.palette.monochrome.monoChrome11, cursor: 'pointer' },
          }}
          onClick={handleClick}
        >
          <VerticalDotsOutlineIcon stroke="#E60000" />
        </Box>
      ) : null}
      {sim.status !== 'PENDING' ? (
        <Box>
          <MenuWrapper
            id={`menu-${sim.id}`}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {renderNewConnectionOption()}
            {renderResendOption()}
            {renderUpgradeOption()}
            {renderSuspendSimOption()}
            {renderTerminateConnection()}
            {renderRestoreOption()}
            {renderCancelProvisionOption()}
            {renderCancelTerminationOption()}
          </MenuWrapper>
        </Box>
      ) : null}
      {showSuspendSim ? (
        <SuspendSimDrawer
          sim={sim}
          open={showSuspendSim}
          onClose={() => setShowSuspendSim(false)}
          onSuspendSimSuccess={onSuspendSimSuccess}
          getDeviceById={getDeviceById}
        />
      ) : null}
      {showUpgradeDevice ? (
        <UpgradeDeviceDrawer
          open={showUpgradeDevice}
          onClose={() => setShowUpgradeDevice(false)}
          onUpgradeSuccess={onUpgradeSuccess}
        />
      ) : null}
      {showNewConnection ? (
        <NewConnectionDrawer
          onClose={() => setShowNewConnection(false)}
          open={showNewConnection}
          onNewConnectionSuccess={onNewConnectionSuccess}
          initialStep={7}
        />
      ) : null}
      {showRestoreConnection ? (
        <RestoreConnectionDrawer
          onClose={() => setShowRestoreConnection(false)}
          open={showRestoreConnection}
          onRestoreConnectionSuccess={onRestoreConnectionSuccess}
          getDeviceById={getDeviceById}
          sim={sim}
        />
      ) : null}
      {showTerminateConnection ? (
        <TerminateConnectionDrawer
          onClose={() => setShowTerminateConnection(false)}
          open={showTerminateConnection}
          sim={sim}
          onTerminateConnectionSuccess={onTerminateConnectionSuccess}
        />
      ) : null}
    </>
  );
}

export default UserDetailsSimRowOptions;
